(() => {
  const script = document.currentScript;
  const endpoint = script.dataset.availabilityEndpoint;
  const storeOnlyText = script.dataset.storeOnlyLabel;
  const cache = new Map();
  const pending = new Map();
  const queued = new Set();
  const original = new WeakMap();
  const variantBadgeRuns = new WeakMap();
  let draining = false;
  let scheduled = false;

  function readIds(value) {
    try {
      return [...new Set(JSON.parse(value).map(String).filter(id => /^[1-9]\d*$/.test(id)))];
    } catch { return []; }
  }

  function check(id) {
    const item = cache.get(id);
    if (item && item.expiresAt > Date.now()) return Promise.resolve(item.value);
    if (pending.has(id)) return pending.get(id).promise;
    let resolve;
    const promise = new Promise(done => { resolve = done; });
    pending.set(id, { promise, resolve });
    queued.add(id);
    if (!draining) {
      draining = true;
      queueMicrotask(drain);
    }
    return promise;
  }

  async function drain() {
    try {
      while (queued.size) {
        const ids = [...queued].slice(0, 100);
        ids.forEach(id => queued.delete(id));
        let results = new Map();
        try {
          const query = encodeURIComponent(JSON.stringify(ids.map(variantId => ({ variantId }))));
          const response = await fetch(`${endpoint}?variants=${query}`, { signal: AbortSignal.timeout(10000) });
          if (!response.ok) throw new Error('availability_unavailable');
          const result = await response.json();
          results = new Map((result.items || []).map(item => [String(item.variantId), item]));
        } catch {}
        for (const id of ids) {
          const value = results.get(id) || { ok: false, available: false };
          cache.set(id, { value, expiresAt: Date.now() + (value.ok ? 60000 : 10000) });
          if (cache.size > 2000) cache.delete(cache.keys().next().value);
          pending.get(id).resolve(value);
          pending.delete(id);
        }
      }
    } finally { draining = false; }
  }

  function setLabel(label, available) {
    if (!original.has(label)) original.set(label, { text: label.textContent, color: label.style.color });
    const fallback = original.get(label);
    const text = available ? storeOnlyText.toLocaleUpperCase() : fallback.text;
    const color = available ? '#b45309' : fallback.color;
    if (label.textContent !== text) label.textContent = text;
    if (label.style.color !== color) label.style.color = color;
  }

  async function updateLabel(label) {
    const raw = label.dataset.dsStoreOnlyVariants;
    const ids = readIds(raw);
    if (!ids.length) return;
    const results = await Promise.all(ids.map(check));
    if (!label.isConnected || label.dataset.dsStoreOnlyVariants !== raw) return;
    setLabel(label, results.some(item => item.ok && item.available));
  }

  function selectedVariant(widget) {
    try {
      const variants = JSON.parse(widget.dataset.variants || '[]');
      const scope = widget.closest('[id^="x-product-template-"]') || widget.closest('.shopify-section') || document;
      const input = scope.querySelector('form[action*="/cart/add"] [name="id"]');
      const id = input?.value || new URLSearchParams(location.search).get('variant');
      return variants.find(variant => String(variant.id) === String(id)) || variants[0];
    } catch { return null; }
  }

  async function updateWidget(widget) {
    const variant = selectedVariant(widget);
    if (!variant || variant.available !== false) return;
    const label = widget.querySelector('.ds-availability__status span:last-child');
    const dot = widget.querySelector('.ds-availability__dot');
    if (!label || !dot) return;
    if (!original.has(label)) original.set(label, { text: label.textContent, muted: dot.classList.contains('ds-availability__dot--muted'), preorder: dot.classList.contains('ds-availability__dot--preorder') });
    const result = await check(String(variant.id));
    if (!label.isConnected || String(selectedVariant(widget)?.id) !== String(variant.id)) return;
    const fallback = original.get(label);
    const storeOnly = result.ok && result.available;
    const text = storeOnly ? storeOnlyText : fallback.text;
    if (label.textContent !== text) label.textContent = text;
    dot.classList.toggle('ds-availability__dot--muted', storeOnly ? false : fallback.muted);
    dot.classList.toggle('ds-availability__dot--preorder', storeOnly ? false : fallback.preorder);
  }

  function setVariantBadge(input, label, available) {
    let badge = label.querySelector('.ds-variant-store-badge');
    label.classList.toggle('ds-variant-option--store-only', available);
    const badgeId = `${input.id}-store-badge`;
    const describedBy = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== badgeId);
    if (available) {
      if (!badge) {
        badge = document.createElement('span');
        badge.id = badgeId;
        badge.className = 'ds-variant-store-badge';
        badge.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 10v10h16V10M3 10l2-6h14l2 6M3 10a3 3 0 0 0 4 2 3 3 0 0 0 5 0 3 3 0 0 0 5 0 3 3 0 0 0 4-2M9 20v-6h6v6"/></svg>';
        const text = document.createElement('span');
        text.textContent = storeOnlyText;
        badge.append(text);
        label.append(badge);
      }
      describedBy.push(badgeId);
    } else {
      badge?.remove();
    }
    if (describedBy.length) input.setAttribute('aria-describedby', describedBy.join(' '));
    else input.removeAttribute('aria-describedby');
  }

  async function updateVariantBadges(fieldset) {
    let variants;
    try { variants = JSON.parse(fieldset.dataset.dsProductVariants || '[]'); }
    catch { return; }
    const run = {};
    variantBadgeRuns.set(fieldset, run);
    const optionIndex = Number(fieldset.dataset.optionIndex);
    const scope = fieldset.closest('.options-container') || fieldset;
    const selections = new Map(Array.from(scope.querySelectorAll('[data-ds-variant-picker]')).map(picker => [
      Number(picker.dataset.optionIndex), picker.querySelector('input.input-radio:checked')?.value
    ]));
    await Promise.all(Array.from(fieldset.querySelectorAll('input.input-radio')).map(async input => {
      const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label) return;
      const ids = input.classList.contains('disabled') ? variants.filter(variant => (
        variant.available === false && variant.options?.[optionIndex] === input.value &&
        Array.from(selections).every(([index, value]) => index === optionIndex || !value || variant.options?.[index] === value)
      )).map(variant => String(variant.id)) : [];
      if (!ids.length) { setVariantBadge(input, label, false); return; }
      const results = await Promise.all(ids.map(check));
      if (!fieldset.isConnected || variantBadgeRuns.get(fieldset) !== run) return;
      setVariantBadge(input, label, results.some(item => item.ok && item.available));
    }));
  }

  function scan() {
    scheduled = false;
    document.querySelectorAll('[data-ds-store-only-variants]').forEach(updateLabel);
    document.querySelectorAll('[data-store-availability]').forEach(updateWidget);
    document.querySelectorAll('[data-ds-variant-picker]').forEach(updateVariantBadges);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(scan, 0);
  }

  function init() {
    if (!endpoint || !storeOnlyText) return;
    scan();
    document.addEventListener('change', schedule);
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
    new MutationObserver(records => {
      if (records.some(record => (record.type === 'attributes' && record.target.matches('[data-ds-variant-picker] input.input-radio')) || [...record.addedNodes].some(node => node.nodeType === 1 && (
        node.matches('[data-ds-store-only-variants], [data-store-availability], .ds-availability, [data-ds-variant-picker]') ||
        node.querySelector('[data-ds-store-only-variants], [data-store-availability], .ds-availability, [data-ds-variant-picker]')
      )))) schedule();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    setInterval(() => { if (!document.hidden) schedule(); }, 60000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
